import { Directive, ElementRef, HostListener, Input, OnDestroy } from '@angular/core';

/**
 * Adds keyboard typeahead functionality to Constellation c11n-dropdown components.
 *
 * Auto-attaches to any <c11n-dropdown> in components that import this directive.
 * When the dropdown is open, typing a letter will jump to the first menu item
 * starting with that letter. Typing the same letter repeatedly cycles through
 * all matching items. Typing multiple letters quickly (within 300ms) performs
 * multi-character matching.
 *
 * @example
 * ```html
 * <c11n-dropdown
 *   [menuData]="stateMenuData"
 *   ...>
 * </c11n-dropdown>
 * ```
 */
@Directive({
  selector: 'c11n-dropdown',
  standalone: true
})
export class DropdownTypeaheadDirective implements OnDestroy {
  private searchBuffer = '';
  private resetTimer: ReturnType<typeof setTimeout> | null = null;
  private lastMatchIndex = -1;
  private lastSearchChar = '';

  /** Timeout in ms before the search buffer resets. Default 300ms. */
  @Input() typeaheadTimeout = 300;

  constructor(private el: ElementRef<HTMLElement>) {}

  @HostListener('keydown', ['$event'])
  // TODO: Refactor to reduce cyclomatic complexity (currently 11, max 10)
  // eslint-disable-next-line complexity
  onKeyDown(event: KeyboardEvent): void {
    // HEYHEY: Heads up that this has a reliance to the Constellation component classes. (Currently using Constellation v17)
    // -> There's a very slight risk if Constellation updates the names below (.c11n-dropdown__trigger, .c11n-dropdown__menu-item, .c11n-menu__item-label) to something else, which might cause this directive to fail silently.
    // Only handle single printable characters (letters, digits, spaces)
    if (event.key.length !== 1 || event.ctrlKey || event.altKey || event.metaKey) {
      return;
    }

    // Check if dropdown is expanded
    const triggerButton = this.el.nativeElement.querySelector(
      '.c11n-dropdown__trigger'
    ) as HTMLButtonElement | null;

    if (!triggerButton || triggerButton.getAttribute('aria-expanded') !== 'true') {
      return;
    }

    // Allow spacebar to confirm selection if a menu item is focused
    if (event.key === ' ' && this.searchBuffer === '' && document.activeElement?.classList.contains('c11n-dropdown__menu-item')) {
      return; // Let the default spacebar behavior trigger the button click
    }

    // Prevent default behavior (like page scrolling on space)
    event.preventDefault();

    // Accumulate typed character
    const typedChar = event.key.toLowerCase();
    this.searchBuffer += typedChar;
    this.resetBufferTimer();

    // Get all menu item buttons and their label texts
    const menuItemButtons = Array.from(
      this.el.nativeElement.querySelectorAll('.c11n-dropdown__menu-item')
    ) as HTMLButtonElement[];

    const labels = menuItemButtons.map(btn => {
      const labelSpan = btn.querySelector('.c11n-menu__item-label');
      return labelSpan?.textContent?.trim().toLowerCase() || '';
    });

    // Find matching item
    const matchIndex = this.findMatch(labels, this.searchBuffer, typedChar);

    if (matchIndex !== -1) {
      menuItemButtons[matchIndex].focus();
      this.lastMatchIndex = matchIndex;
      this.lastSearchChar = typedChar;
    }
  }

  /**
   * Find the index of the menu item to focus based on the search buffer.
   */
  private findMatch(labels: string[], search: string, typedChar: string): number {
    // Check if user is repeating the same character (e.g., "mmm")
    const isRepeatingChar = search.length > 0 && search === search[0].repeat(search.length);

    // If single character OR repeating same character, cycle through single-char matches
    if (search.length === 1 || (isRepeatingChar && typedChar === this.lastSearchChar)) {
      const char = search[0];
      const matchingIndices = labels
        .map((label, i) => label.startsWith(char) ? i : -1)
        .filter(i => i !== -1);

      if (matchingIndices.length === 0) return -1;

      // If cycling (repeated same char), advance to next match
      if (isRepeatingChar && typedChar === this.lastSearchChar) {
        const currentPos = matchingIndices.indexOf(this.lastMatchIndex);
        if (currentPos !== -1) {
          const nextPos = (currentPos + 1) % matchingIndices.length;
          return matchingIndices[nextPos];
        }
      }

      // First match for this character
      return matchingIndices[0];
    }

    // Multi-character search: find first item starting with the full buffer
    const exactMatch = labels.findIndex(label => label.startsWith(search));
    if (exactMatch !== -1) return exactMatch;

    // If no multi-char match, try just the last typed character
    // (handles case where user switched to a new character)
    return labels.findIndex(label => label.startsWith(typedChar));
  }

  private resetBufferTimer(): void {
    if (this.resetTimer) {
      clearTimeout(this.resetTimer);
    }
    this.resetTimer = setTimeout(() => {
      this.searchBuffer = '';
      this.lastMatchIndex = -1;
      this.lastSearchChar = '';
    }, this.typeaheadTimeout);
  }

  ngOnDestroy(): void {
    if (this.resetTimer) {
      clearTimeout(this.resetTimer);
    }
  }
}
